import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X } from "lucide-react";
import { API } from "@/lib/api";

const getSession = () => {
  let id = localStorage.getItem("kgs_chat_session");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("kgs_chat_session", id); }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "KGS POWER TERMINAL online. Tell me your load or site type — I will point you to the right DG set." },
  ]);
  const bodyRef = useRef(null);

  useEffect(() => { bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }); }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSession(), message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) setMessages((m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, content: msg.content + j.delta } : msg)));
            if (j.error) setMessages((m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, content: j.error } : msg)));
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, content: "Connection issue. Please call +91 69138 28054." } : msg)));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="chat-open-button" className="chat-fab" onClick={() => setOpen(!open)}>
        <span className="chat-dot" /> {open ? <X size={15} /> : "TERMINAL"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="chat-panel" className="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}>
            <div className="chat-head"><span className="chat-dot" /> KGS POWER TERMINAL <em>AI ASSISTANT</em></div>
            <div className="chat-body" ref={bodyRef} data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "chat-msg user" : "chat-msg ai"}>
                  <span>{m.role === "user" ? "YOU" : "KGS"}</span>
                  <p>{m.content}{busy && i === messages.length - 1 && m.role === "assistant" ? "▌" : ""}</p>
                </div>
              ))}
            </div>
            <form className="chat-input-row" onSubmit={send}>
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about capacity, pricing, rental…" />
              <button data-testid="chat-send-button" type="submit" disabled={busy} aria-label="Send"><Send size={15} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
